import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Data, DocumentGroup, Salesforce } from './data.model';
import { HttpClient } from '@angular/common/http';
import { Subject, Observable } from 'rxjs';
import { Md5 } from 'ts-md5';

export interface Loading {
  isShow: boolean;
  message: string;
  update: Function;
  hide: Function;
}

@Injectable()
export class DataService {
  static EXCLUDED_NAMES_OF_DOCUMENT_FOLDERS = ['Documents', 'Archive', 'Trash Bin'];

  private data: Data = new Data();
  private document_group: DocumentGroup = new DocumentGroup();

  private analyticsData: any = {
    databinding: []
  };

  // template_group_id from Document Group Templates item
  private document_group_template_id: any = null;
  private contact_list: any = [];
  // Merge Attachments Document
  private mergedDocumentId: string = '';

  private record_list: any = [];
  private bulk_type: any = [];

  private salesforce: Salesforce = new Salesforce();
  private record_emails: any = [];
  private sn_token: string = '';
  private self_signer_client_email: string = '';

  private baseUrl = '';
  private env: any;
  public salesforce_session_error: string =
    'Invalid Salesforce authentication data. Accounts app permissions could be set improperly. Please check "Enable Connected Apps" section in the SignNow Salesforce User Guide on the appexchange listing.';

  private attachment_group_doc_ids: any[];

  private sending_type: string = '';

  private loading: Loading = {
    isShow: false,
    message: '',
    update(state: boolean, msg: string) {
      this.isShow = state;
      this.message = msg;
    },
    hide() {
      this.update(false, '');
    }
  };

  constructor(private http: HttpClient, private router: Router) {
    this.env = 'DEV';
    if (this.env === 'DEV') {
      this.baseUrl = '';
    } else if (this.env === 'PROD') {
      this.baseUrl = '';
    }
  }

  getLoading() {
    return this.loading;
  }

  setMergeAttachmentDocId(id: string) {
    this.mergedDocumentId = id;
  }
  getMergeAttachmentDocId() {
    return this.mergedDocumentId;
  }

  setAttachmentGroupDocIds(ids: any[]) {
    this.attachment_group_doc_ids = ids;
  }
  getAttachmentGroupDocIds() {
    return this.attachment_group_doc_ids;
  }

  setSelfSignerClientEmail(email) {
    this.self_signer_client_email = email;
  }
  getSelfSignerClientEmail() {
    return this.self_signer_client_email;
  }

  AddToContactList(contact: any) {
    this.contact_list.push(contact);
  }
  setContactList(contacts) {
    this.contact_list = contacts;
  }
  getContactList() {
    return this.contact_list;
  }
  RemoveFromContactList(contact) {
    for (var i = 0; i < this.contact_list.length; i++) {
      if (this.contact_list[i].Id == contact.Id) {
        this.contact_list.splice(i, 1);
        return;
      }
    }
  }

  addToRecordList(record: any) {
    this.record_list.push(record);
  }
  setRecordList(records) {
    this.record_list = records;
  }
  getRecordList() {
    return this.record_list;
  }
  removeFromRecordList(record) {
    for (let i = 0; i < this.record_list.length; i++) {
      if (this.record_list[i].Id === record.Id) {
        this.record_list.splice(i, 1);
        return;
      }
    }
  }

  AddTemplateToDocumentGroup(template_id: string) {
    this.document_group.template_ids.push(template_id);
  }

  setSNToken(sn_token: string) {
    this.sn_token = sn_token;
  }
  getSNToken(): string {
    return this.sn_token;
  }

  RemoveTemplateFromGroup(template_id: string) {
    for (var i = 0; i < this.document_group.template_ids.length; i++) {
      if (this.document_group.template_ids[i] == template_id) {
        this.document_group.template_ids.splice(i, 1);
        return;
      }
    }
  }
  clearDocumentGroup() {
    this.document_group.template_ids = [];
  }
  setSelfSigner(self_signer: boolean) {
    this.data.self_signer = self_signer;
  }
  getDocumentGroup(): DocumentGroup {
    return this.document_group;
  }

  getSalesforceData(): Salesforce {
    this.maintenanceCheck(this.salesforce);

    if (this.salesforce.sf_user_id) {
      return this.salesforce;
    }

    this.loadSalesforceData();
    return this.salesforce;
  }

  setSignNowOwnerEmail(email) {
    this.data.sn_email = email;
  }
  getSignNowOwnerEmail(): any {
    return this.data.sn_email;
  }

  setSalesforceRefresh(sf_refresh: string) {
    this.salesforce.sf_refresh = sf_refresh;
  }
  setSalesforceToken(sf_token: string) {
    this.salesforce.sf_token = sf_token;
  }
  setSalesforceInstanceUrl(url: string) {
    this.salesforce.instance_url = url;
  }
  setSalesforceRecordType(type: string) {
    this.salesforce.record_type = type;
  }
  getSalesforceRecordType() {
    return this.salesforce.record_type;
  }
  setSalesforceRecordId(id: string) {
    this.salesforce.record_id = id;
  }
  setSalesforceViewParam(view: string) {
    this.salesforce.view = view;
  }
  setMaintenance(is_maintenance: boolean) {
    this.salesforce.is_maintenance = is_maintenance;
  }
  setProxyUrl(proxy_url: string) {
    this.salesforce.proxy_url = proxy_url;
  }
  setSalesforceContext(context) {
    try {
      if (context.length > 0) {
        this.salesforce.context = context;
        this.salesforce.location_url = JSON.parse(context).environment.locationUrl;
      }
    } catch {}
  }

  setSalesforceUserId(user_id: string) {
    this.salesforce.sf_user_id = user_id;
  }

  setSalesforceOrgId(org_id: string) {
    this.salesforce.sf_org_id = org_id;
  }

  valid_salesforce_session(salesforce_session_data) {
    if (salesforce_session_data.view === 'status' || salesforce_session_data.view === 'document_flow') {
      return true;
    }

    const sfSessionDataKeys = Object.keys(salesforce_session_data).filter((key) => key !== 'is_maintenance');
    return sfSessionDataKeys.length && sfSessionDataKeys.every((key) => salesforce_session_data[key]);
  }

  maintenanceCheck(salesforce_session_data) {
    if (salesforce_session_data && salesforce_session_data.is_maintenance) {
      this.router.navigate(['maintenance']);
    }
  }

  apiGetCall(url, options: any = '') {
    try {
      if (!options) {
        return this.http.get<any>(this.baseUrl + url);
      } else {
        return this.http.get<any>(this.baseUrl + url, options);
      }
    } catch (error) {
      alert(error);
    }
  }
  apiPostCall(url, data: any) {
    try {
      return this.http.post<any>(this.baseUrl + url, data);
    } catch (error) {
      alert(error);
    }
  }

  /*******************************************************************************/
  async loadSalesforceData() {
    const salesforce_session_data = await this.apiGetCall('/api/salesforce_session_data').toPromise();

    this.maintenanceCheck(salesforce_session_data);

    const sf_token = salesforce_session_data.sf_token;
    const sf_refresh = salesforce_session_data.sf_refresh;
    const sf_org_id = salesforce_session_data.sf_org_id;
    const sf_user_id = salesforce_session_data.sf_user_id;
    const instance_url = salesforce_session_data.instance_url;
    const record_type = salesforce_session_data.record_type;
    const record_id = salesforce_session_data.record_id;
    const context = salesforce_session_data.context;
    const view = salesforce_session_data.view || '';
    const is_maintenance = salesforce_session_data.is_maintenance;
    const proxy_url = salesforce_session_data.proxy_url;

    // set information in data service
    this.setSalesforceRefresh(sf_refresh);
    this.setSalesforceToken(sf_token);
    this.setSalesforceOrgId(sf_org_id);
    this.setSalesforceUserId(sf_user_id);
    this.setSalesforceInstanceUrl(instance_url);
    this.setSalesforceRecordType(record_type);
    this.setSalesforceRecordId(record_id);
    this.setSalesforceContext(context);
    this.setSalesforceViewParam(view);
    this.setMaintenance(is_maintenance);
    this.setProxyUrl(proxy_url);
    return this.salesforce;
  }

  /*******************************************************************************/

  getData(): Data {
    // Return the entire Form Data
    return this.data;
  }

  reinitialize() {
    this.data = new Data();
    this.document_group = new DocumentGroup();
    this.salesforce = new Salesforce();
    this.record_emails = [];
  }

  setOriginTemplateId(origin_template_id: string) {
    this.data.origin_template_id = origin_template_id;
  }
  setDocumentId(document_id: string) {
    this.data.document_id = document_id;
  }

  setRoutingDetails(_document: any) {
    const routingDetails = _document.routing_details ? Object.assign(_document.routing_details) : [];
    const documentFields = _document.fields;

    // searching if payment_request is use and set field id for this purpose
    for (let i = 0; i < routingDetails.length; ++i) {
      if (routingDetails[i].data) {
        routingDetails[i].data.forEach((routingDetailsItem) => {
          if (routingDetailsItem.payment_request && routingDetailsItem.payment_request.field_name) {
            documentFields.forEach((field) => {
              if (field.json_attributes.name === routingDetailsItem.payment_request.field_name) {
                routingDetailsItem.payment_request.field_id = field.id;
              }
            });
          }
        });
      }
    }

    this.data.routing_details = routingDetails;
  }

  setDocumentOwner(email: string) {
    this.data.document_owner_email = email;
  }

  setOriginView(origin_view: string) {
    this.data.origin_view = origin_view;
  }
  getOriginView(): string {
    return this.data.origin_view;
  }

  resetData(): Data {
    // Reset the workflow
    // Return the form data after all this.* members had been reset
    this.data.clear();
    this.clearDocumentGroup();
    this.resetAnalyticsData();
    return this.data;
  }

  setDocumentName(document_name: string) {
    this.data.document_name = document_name;
  }

  documentGroupMode(mode: boolean) {
    this.data.document_group_active = mode;
  }

  usingDocumentGroup(): boolean {
    return this.data.document_group_active;
  }

  setGroupName(group_name: string) {
    this.document_group.document_group_name = group_name;
  }

  setDocumentGroupDocIds(document_ids: any) {
    this.document_group.documents = document_ids;
  }

  setDocumentGroupId(group_id: string) {
    this.document_group.document_group_id = group_id;
  }

  addAnnotationsToDocument() {
    var salesforce_annotation_data = {
      sf_token: this.salesforce.sf_token,
      instance_url: this.salesforce.instance_url,
      record_type: this.salesforce.record_type,
      record_id: this.salesforce.record_id,
      annotations: this.data.annotations
    };
    this.apiPostCall(
      '/api/add_annotations_to_document?document_id=' + this.data.document_id,
      salesforce_annotation_data
    ).subscribe((result) => {
      var res: any = JSON.parse(result);
      return res;
    });
  }

  setRecordEmails(emails) {
    this.record_emails = emails;
  }

  getRecordEmails() {
    return this.record_emails;
  }

  getUrlFromSFContext() {
    return this.salesforce.location_url;
  }

  getUrlParamater(str) {
    try {
      var name = str.toString();
      name = name.replace(/[\[]/, '\\[').replace(/[\]]/, '\\]');
      var regexS = '[\\?&]' + name + '=([^&#]*)';
      var regex = new RegExp(regexS);
      var results = regex.exec(this.getUrlFromSFContext());
      if (results == null) {
        return '';
      } else {
        return results[1];
      }
    } catch {
      return '';
    }
  }

  getFieldDataFromText(text) {
    var regExp = /{(.*?)}/g;
    var field_type, field_value, temp;
    var field_value_list = [];
    do {
      field_type = regExp.exec(text);
      if (field_type) {
        temp = field_type[1].split('.');
        field_value = { field: temp[1], type: temp[0], value: '' };
        field_value_list.push(field_value);
      }
    } while (field_type);
    return field_value_list;
  }

  setTemplateSetting(template_setting) {
    this.data.template_setting = template_setting;
    this.data.using_template_setting = true;
  }

  setAnnotations(annotations: any) {
    this.data.annotations = annotations;
  }

  getSalesforceAnnotationsFromDocument(document_id: string) {
    this.apiGetCall('/api/get_document_annotations?document_id=' + document_id).subscribe((result) => {
      var res: any = JSON.parse(result);
      var salesforce_annotations: any = res.integration_objects;

      if (salesforce_annotations != null) {
        for (var i = 0; i < salesforce_annotations.length; i++) {
          this.data.annotations.push(salesforce_annotations[i]);
        }
      }
    });
  }

  setDocumentGroupData(data) {
    this.document_group.document_group_data = data;
  }

  getDocumentGroupData() {
    return this.document_group.document_group_data;
  }

  saveSentDocumentGroupAsNotesAndAttachments(document_roles) {
    this.getUserRole().subscribe(
      (userRole: string) => {
        if (userRole && userRole.toLocaleLowerCase() === 'system administrator') {
          this.getOrgAdminSettingsById(this.getSalesforceData().sf_org_id).subscribe(
            (orgAdminSettings: any) => {
              // if "is_not_save_document" flag === false or we have't org_admin_settings in db
              if (
                (orgAdminSettings && orgAdminSettings.is_not_save_document === false) ||
                orgAdminSettings === null
              ) {
                const document_group_data = document_roles;
                let count = 0;
                for (let i = 0; i < document_group_data.length; i++) {
                  const data = {
                    sf_token: this.salesforce.sf_token,
                    sf_org_id: this.salesforce.sf_org_id,
                    instance_url: this.salesforce.instance_url,
                    record_id: this.salesforce.record_id,
                    document_id: document_group_data[i].document_id,
                    document_name: document_group_data[i].document_name + ' (sent)'
                  };
                  this.apiPostCall('/api/download_document', data).subscribe(() => {
                    count++;
                    if (count === document_group_data.length) {
                    }
                  });
                }
              }
            },
            (err) => {
              console.log('err', err);
            }
          );
        }
      },
      (err) => {
        console.log('error', err);
      }
    );
  }

  saveSentDocumentAsNotesAndAttachments() {
    const data = {
      sf_token: this.salesforce.sf_token,
      sf_org_id: this.salesforce.sf_org_id,
      instance_url: this.salesforce.instance_url,
      record_id: this.salesforce.record_id,
      document_id: this.data.document_id,
      document_name: this.data.document_name + ' (sent)'
    };
    this.apiPostCall('/api/download_document', data).subscribe(() => {});
  }

  addDocumentAnnotationInDocumentGroup(document_annotation) {
    this.document_group.annotations.push(document_annotation);
  }
  clearDocumentAnnotationInDocumentGroup() {
    this.document_group.annotations = [];
  }

  getDocumentGroupAnnoations() {
    return this.document_group.annotations;
  }

  setSubjectAndBody(subject: string, body: string) {
    this.data.invite_message_subject = subject;
    this.data.invite_message_body = body;
  }

  setRoles(roles: any) {
    this.data.roles = roles;
  }

  getRoutingDetails() {
    return this.data.routing_details;
  }

  getUserRole(): Observable<string> {
    const userSFData = this.getSalesforceData();
    const subject$ = new Subject<string>();

    if (userSFData && userSFData.sf_user_id && userSFData.sf_token !== '') {
      const params = {
        sf_token: userSFData.sf_token,
        instance_url: userSFData.instance_url,
        user_id: userSFData.sf_user_id
      };

      return this.apiGetCall('/api/get_user_role', { params });
    } else {
      setTimeout(() => {
        subject$.next('null');
      }, 1000);
    }

    return subject$;
  }

  getOrgAdminSettingsById(sf_org_id) {
    const params = {
      sf_org_id: sf_org_id
    };

    return this.apiGetCall('/api/get_org_admin_settings_by_id', { params });
  }

  getAllLayoutsList(): Observable<any> {
    const userSFData = this.getSalesforceData();
    const subject$ = new Subject<string>();

    if (userSFData && userSFData.sf_user_id && userSFData.sf_token !== '') {
      const params = {
        sf_token: userSFData.sf_token,
        instance_url: userSFData.instance_url
      };

      return this.apiGetCall('/api/get_all_layouts', { params });
    } else {
      setTimeout(() => {
        subject$.next('null');
      }, 1000);

      return subject$;
    }
  }

  getAllSalesforceObjectsList(): Observable<any> {
    const userSFData = this.getSalesforceData();
    const subject$ = new Subject<string>();

    if (userSFData && userSFData.sf_user_id && userSFData.sf_token !== '') {
      const params = {
        sf_token: userSFData.sf_token,
        instance_url: userSFData.instance_url
      };

      return this.apiGetCall('/api/get_all_sf_objects_list', { params });
    } else {
      setTimeout(() => {
        subject$.next('null');
      }, 1000);

      return subject$;
    }
  }

  async get_document(document_id) {
    return await this.apiGetCall('/api/get_document?document_id=' + document_id).toPromise();
  }

  getDocumentGroupTemplateId() {
    return this.document_group_template_id;
  }
  setDocumentGroupTemplateId(document_group_template_id: string) {
    this.document_group_template_id = document_group_template_id;
  }
  unsetDocumentGroupTemplateId(): void {
    this.document_group_template_id = null;
  }

  setSendingType(sendingType: string) {
    this.sending_type = sendingType;
  }

  getSendingType(): string {
    return this.sending_type;
  }

  md5hash(data) {
    return data ? Md5.hashStr(data) : null;
  }

  addAnalyticsDatabindingInfo(databindingInfo: { is_append: boolean; is_update: boolean }) {
    this.analyticsData.databinding.push(databindingInfo);
  }

  getAnalyticsDatabindingInfo(): { is_append: boolean; is_update: boolean } {
    return this.analyticsData.databinding;
  }

  resetAnalyticsData() {
    this.analyticsData = {
      databinding: []
    };
  }

  isAllowedFolder(folderName: string): boolean {
    return !DataService.EXCLUDED_NAMES_OF_DOCUMENT_FOLDERS.includes(folderName);
  }

  /**
   * add folder to folderList copy and return sorted copy
   * also, documents in folder will be sorted by alphabet
   *
   * @param foldersList - array of folders with documents
   * @param folder
   */
  addFolderAndSort(foldersList, folder) {
    let foldersToSort = foldersList.slice();

    // sorting documents in folder by alphabet
    if (folder.documents && folder.documents.length) {
      folder.documents.sort((a, b) => {
        const labelA = a.document_name.toLowerCase();
        const labelB = b.document_name.toLowerCase();

        if (labelA < labelB) {
          return -1;
        }
        if (labelA > labelB) {
          return +1;
        }

        return 0;
      });
    }

    // sorting folders. 1st place for Templates folders, then by alphabet
    if (folder.name === 'Templates') {
      foldersToSort.unshift(folder);
    } else if (folder.name === 'Team Templates') {
      let isMainTemplatesFolderInArray = false;
      let onlyTeamFolders = [];

      if (foldersToSort.length && foldersToSort[0].name === 'Templates') {
        onlyTeamFolders = foldersToSort.slice(1);
        isMainTemplatesFolderInArray = true;
      } else {
        onlyTeamFolders = foldersToSort.length ? foldersToSort.slice() : [];
      }

      folder.team_name = folder.shared ? folder.team_name + ' (Shared)' : folder.team_name;

      onlyTeamFolders.push(folder);
      onlyTeamFolders.sort((a, b) => {
        const labelA = a.team_name?.toLowerCase() || '';
        const labelB = b.team_name?.toLowerCase() || '';

        if (labelA < labelB) {
          return -1;
        }
        if (labelA > labelB) {
          return +1;
        }

        return 0;
      });

      if (isMainTemplatesFolderInArray) {
        foldersToSort = [foldersToSort[0], ...onlyTeamFolders];
      } else {
        foldersToSort = onlyTeamFolders;
      }
    }

    return foldersToSort;
  }
}
